// frontend/src/pages/admin/AdminFundWallet.js
import React, { useState, useEffect, useMemo } from 'react';
import { usersApi } from '../../api/users';
import { walletApi } from '../../api/wallet';
import helpers from '../../utils/helpers';
import {
  FaWallet, FaSpinner, FaSearch, FaCheckCircle, FaUserCircle, FaArrowLeft, FaExclamationTriangle, FaMoneyCheckAlt
} from 'react-icons/fa';

// Must match the backend guard (MAX_MANUAL_CREDIT in wallet_service.py).
const MAX_TOPUP = 5000000;

// 1500 -> "One Thousand, Five Hundred Naira". Mirrors the backend so the
// confirmation shows the same words the customer/admins get alerted with.
const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const underThousand = (n) => {
  if (n === 0) return '';
  if (n < 20) return ONES[n];
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? '-' + ONES[n % 10] : '');
  return ONES[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + underThousand(n % 100) : '');
};
const nairaInWords = (amount) => {
  let n = Math.round(Number(amount) || 0);
  if (n === 0) return 'Zero Naira';
  const scales = [[1000000000, 'billion'], [1000000, 'million'], [1000, 'thousand']];
  const parts = [];
  for (const [value, name] of scales) {
    if (n >= value) { parts.push(underThousand(Math.floor(n / value)) + ' ' + name); n %= value; }
  }
  if (n) parts.push(underThousand(n));
  const words = parts.map(p => p.trim()).filter(Boolean).join(', ');
  return words.charAt(0).toUpperCase() + words.slice(1) + ' Naira';
};

const displayName = (u) =>
  (u.full_name && u.full_name.trim()) || u.company_name || u.phone ||
  (u.email && !u.email.endsWith('@phone.user') ? u.email : '') || 'Unnamed customer';

const contactLine = (u) => {
  const bits = [];
  if (u.email && !u.email.endsWith('@phone.user')) bits.push(u.email);
  if (u.phone) bits.push(u.phone);
  return bits.join('  •  ') || 'No contact on file';
};

function AdminFundWallet() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  // "I have paid" claims
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [busyClaim, setBusyClaim] = useState(null);

  const loadClaims = async () => {
    try {
      const data = await walletApi.getTopupClaims();
      setClaims(Array.isArray(data) ? data : []);
    } catch (err) {
      /* non-fatal: the search/fund tool still works without the list */
    } finally {
      setClaimsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await usersApi.getAllUsersAdmin();
        setUsers(Array.isArray(data) ? data : (data?.users || []));
      } catch (err) {
        setError('Could not load the customer list. Please refresh.');
      } finally {
        setLoading(false);
      }
    })();
    loadClaims();
  }, []);

  const pendingClaims = claims.filter(c => c.status === 'pending');
  const historyClaims = claims.filter(c => c.status !== 'pending');

  const approveClaim = async (c) => {
    if (!window.confirm(`Confirm you have received ${helpers.formatCurrency(c.amount, 'NGN')} from ${c.user_name} before crediting their wallet.`)) return;
    setBusyClaim(c.id); setError('');
    try {
      const res = await walletApi.approveTopupClaim(c.id);
      setDone({ name: c.user_name, amount: c.amount, message: res?.message });
      await loadClaims();
    } catch (err) {
      setError(err?.message || 'Could not approve this claim.');
    } finally {
      setBusyClaim(null);
    }
  };

  const declineClaim = async (c) => {
    const note = window.prompt(`Decline ${c.user_name}'s claim of ${helpers.formatCurrency(c.amount, 'NGN')}?\nOptional reason (the customer sees this):`, '');
    if (note === null) return; // cancelled
    setBusyClaim(c.id); setError('');
    try {
      await walletApi.declineTopupClaim(c.id, note);
      await loadClaims();
    } catch (err) {
      setError(err?.message || 'Could not decline this claim.');
    } finally {
      setBusyClaim(null);
    }
  };

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return users.filter(u =>
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q) ||
      (u.company_name || '').toLowerCase().includes(q)
    ).slice(0, 25);
  }, [query, users]);

  const amountNum = parseFloat(amount);
  const amountValid = !isNaN(amountNum) && amountNum > 0 && amountNum <= MAX_TOPUP;

  const startConfirm = () => {
    setError('');
    if (!selected) { setError('Please choose a customer first.'); return; }
    if (isNaN(amountNum) || amountNum <= 0) { setError('Enter an amount greater than zero.'); return; }
    if (amountNum > MAX_TOPUP) {
      setError(`For safety, a single top-up cannot exceed ${helpers.formatCurrency(MAX_TOPUP, 'NGN')}. Split a larger amount into separate top-ups.`);
      return;
    }
    if (!reason.trim()) { setError('Enter a reason (e.g. "Bank transfer received").'); return; }
    setConfirming(true);
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await walletApi.manualCredit({
        user_id: selected.id,
        amount: amountNum,
        reason: reason.trim(),
      });
      setDone({ name: displayName(selected), amount: amountNum, message: res?.message });
      setConfirming(false);
      setSelected(null);
      setAmount('');
      setReason('');
      setQuery('');
    } catch (err) {
      setError(err?.message || 'Could not fund the wallet. Please try again.');
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-500">
        <FaSpinner className="animate-spin text-3xl text-primary mx-auto mb-2" /> Loading customers...
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6">
      <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-1">
        <FaWallet className="text-green-600" /> Fund a Customer's Wallet
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        Use this after a customer has paid you by bank transfer. Their Spending Wallet
        is credited at once, and the customer plus all admins are notified.
      </p>

      {done && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <FaCheckCircle className="text-green-600 text-xl mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold text-green-800">
              {helpers.formatCurrency(done.amount, 'NGN')} added to {done.name}'s wallet.
            </p>
            <p className="text-sm text-green-700">The customer and all admins have been notified.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm font-semibold flex items-start gap-2">
          <FaExclamationTriangle className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {/* --- PENDING "I HAVE PAID" CLAIMS --- */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-1">
          <FaMoneyCheckAlt className="text-blue-600" /> Payment claims
          {pendingClaims.length > 0 && (
            <span className="ml-1 text-xs font-bold bg-red-500 text-white rounded-full px-2 py-0.5">
              {pendingClaims.length} pending
            </span>
          )}
        </h2>
        <p className="text-sm text-gray-500 mb-3">
          Customers who tapped "I have paid". Check your bank, then Approve to credit them, or Decline.
        </p>

        {claimsLoading ? (
          <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
            <FaSpinner className="animate-spin" /> Loading claims...
          </div>
        ) : pendingClaims.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No pending claims right now.</p>
        ) : (
          <div className="space-y-3">
            {pendingClaims.map(c => (
              <div key={c.id} className="border border-blue-100 bg-blue-50 rounded-lg p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-800 break-words">{c.user_name}</p>
                    <p className="text-xs text-gray-600 break-words">
                      {[c.user_phone, c.user_email].filter(Boolean).join('  •  ') || 'No contact on file'}
                    </p>
                    {c.bank_label && <p className="text-xs text-gray-500 mt-0.5">Paid into: {c.bank_label}</p>}
                    <p className="text-[11px] text-gray-400 mt-0.5">{new Date(c.created_at).toLocaleString()}</p>
                  </div>
                  <div className="text-lg font-bold text-blue-900 whitespace-nowrap">
                    {helpers.formatCurrency(c.amount, 'NGN')}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <button
                    onClick={() => approveClaim(c)}
                    disabled={busyClaim === c.id}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-bold px-4 py-2 rounded"
                  >
                    {busyClaim === c.id ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Approve &amp; credit
                  </button>
                  <button
                    onClick={() => declineClaim(c)}
                    disabled={busyClaim === c.id}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60 text-sm font-bold px-4 py-2 rounded"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {historyClaims.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <button
              onClick={() => setShowHistory(v => !v)}
              className="text-sm font-semibold text-blue-600 hover:underline"
            >
              {showHistory ? 'Hide' : 'Show'} history ({historyClaims.length})
            </button>
            {showHistory && (
              <div className="mt-3 space-y-2">
                {historyClaims.map(c => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-gray-50 pb-2">
                    <div className="min-w-0">
                      <span className="font-semibold text-gray-700">{c.user_name}</span>
                      <span className="text-gray-400"> — {new Date(c.created_at).toLocaleDateString()}</span>
                      {c.admin_note && <span className="text-gray-400 block text-xs">Note: {c.admin_note}</span>}
                    </div>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <span className="text-gray-700">{helpers.formatCurrency(c.amount, 'NGN')}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        {c.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- FIND & FUND (kept, for direct crediting) --- */}
      <h2 className="text-lg font-bold text-gray-800 mb-2">Or fund a customer directly</h2>

      {/* STEP 1: choose a customer */}
      {!selected ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-bold text-gray-700 mb-2">1. Find the customer</label>
          <div className="relative">
            <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email or phone"
              className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>

          {query.trim() && (
            <div className="mt-3 divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {results.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No customer matches "{query}".</p>
              ) : results.map(u => (
                <button
                  key={u.id}
                  onClick={() => { setSelected(u); setDone(null); setError(''); }}
                  className="w-full flex items-center gap-3 py-3 px-2 text-left hover:bg-gray-50 rounded"
                >
                  <FaUserCircle className="text-gray-300 text-2xl flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{displayName(u)}</p>
                    <p className="text-xs text-gray-500 truncate">{contactLine(u)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          {/* chosen customer */}
          <div className="flex items-center justify-between gap-3 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3 min-w-0">
              <FaUserCircle className="text-gray-300 text-3xl flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-bold text-gray-800 truncate">{displayName(selected)}</p>
                <p className="text-xs text-gray-500 truncate">{contactLine(selected)}</p>
              </div>
            </div>
            <button
              onClick={() => { setSelected(null); setConfirming(false); }}
              className="text-sm text-blue-600 hover:underline flex items-center gap-1 flex-shrink-0"
            >
              <FaArrowLeft /> Change
            </button>
          </div>

          {/* amount + reason */}
          <div className="pt-4 space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">2. Amount to add (NGN)</label>
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(''); }}
                placeholder="e.g. 34999"
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none text-lg"
              />
              {amount && amountValid && (
                <p className="text-xs text-gray-600 mt-1">{nairaInWords(amountNum)}</p>
              )}
              {amount && !isNaN(amountNum) && amountNum > MAX_TOPUP && (
                <p className="text-xs text-red-600 mt-1 font-semibold">
                  Over the {helpers.formatCurrency(MAX_TOPUP, 'NGN')} single top-up limit.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">3. Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => { setReason(e.target.value); setError(''); }}
                placeholder="e.g. Bank transfer received - GTBank ref 12345"
                maxLength={140}
                className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">The customer sees this, so keep it clear.</p>
            </div>

            <button
              onClick={startConfirm}
              disabled={!amountValid || !reason.trim()}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
            >
              <FaWallet /> Review &amp; Fund Wallet
            </button>
          </div>
        </div>
      )}

      {/* CONFIRM MODAL */}
      {confirming && selected && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Confirm wallet funding</h3>
            <p className="text-gray-700 mb-1">
              You are about to add <strong>{helpers.formatCurrency(amountNum, 'NGN')}</strong> to:
            </p>
            <p className="text-gray-900 font-bold">{displayName(selected)}</p>
            <p className="text-sm text-gray-500 mb-3">{contactLine(selected)}</p>
            <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700 mb-4">
              <p><strong>Amount in words:</strong> {nairaInWords(amountNum)}</p>
              <p className="mt-1"><strong>Reason:</strong> {reason.trim()}</p>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              This credits real money to the customer's wallet and cannot be undone here.
              The customer and all admins will be notified.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={submit}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-bold py-2.5 rounded flex items-center justify-center gap-2"
              >
                {submitting ? <><FaSpinner className="animate-spin" /> Funding...</> : <><FaCheckCircle /> Yes, add the money</>}
              </button>
              <button
                onClick={() => setConfirming(false)}
                disabled={submitting}
                className="flex-1 border border-gray-300 text-gray-700 font-bold py-2.5 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminFundWallet;
